import { coverageColor, stateAbbrToFips } from '@mollusk/shared';

/**
 * Pure helpers that join coverage numbers onto GeoJSON geometry for the
 * choropleth layers, filter counties to a selected state, and compute bounds
 * for fly-to. Kept independent of Mapbox + topojson-client so they can be
 * unit-tested with plain GeoJSON fixtures.
 */

type Feature = GeoJSON.Feature;

/** Map state FIPS → coverage fraction from coverage rows keyed by USPS abbr. */
export function coverageByStateFips(
  states: ReadonlyArray<{ state: string; coverage: number }>,
): Map<string, number> {
  const m = new Map<string, number>();
  for (const s of states) {
    const fips = stateAbbrToFips(s.state);
    if (fips) m.set(fips, s.coverage);
  }
  return m;
}

/** us-atlas feature id is the FIPS code (state: 2-digit, county: 5-digit). */
export const byFeatureId = (f: Feature): string | undefined =>
  f.id == null ? undefined : String(f.id);

/** Our jurisdiction geojson carries the place GEOID in properties. */
export const byPlaceGeoid = (f: Feature): string | undefined => {
  const v = f.properties?.place_geoid;
  return typeof v === 'string' ? v : undefined;
};

/**
 * Attach `coverage`, `color`, and `covered` to each feature's properties,
 * looking coverage up by `keyOf`. Missing keys default to 0 (uncovered).
 */
export function paintFeatures(
  features: ReadonlyArray<Feature>,
  keyOf: (f: Feature) => string | undefined,
  coverageByKey: ReadonlyMap<string, number>,
  opts: { coveredThreshold?: number } = {},
): GeoJSON.FeatureCollection {
  const threshold = opts.coveredThreshold ?? 0;
  return {
    type: 'FeatureCollection',
    features: features.map((f) => {
      const key = keyOf(f);
      const coverage = (key != null ? coverageByKey.get(key) : undefined) ?? 0;
      return {
        ...f,
        properties: {
          ...(f.properties ?? {}),
          coverage,
          color: coverageColor(coverage),
          covered: coverage > threshold,
        },
      };
    }),
  };
}

/** Counties whose 5-digit FIPS falls in the given 2-digit state FIPS. */
export function countiesInState(features: ReadonlyArray<Feature>, stateFips: string): Feature[] {
  return features.filter((f) => f.id != null && String(f.id).startsWith(stateFips));
}

/** Lng/lat bounds [[w,s],[e,n]] enclosing all features, or null if empty. */
export function featuresBounds(
  features: ReadonlyArray<Feature>,
): [[number, number], [number, number]] | null {
  let w = Number.POSITIVE_INFINITY;
  let s = Number.POSITIVE_INFINITY;
  let e = Number.NEGATIVE_INFINITY;
  let n = Number.NEGATIVE_INFINITY;

  const visit = (coords: unknown): void => {
    if (!Array.isArray(coords)) return;
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      const [lng, lat] = coords as [number, number];
      if (lng < w) w = lng;
      if (lng > e) e = lng;
      if (lat < s) s = lat;
      if (lat > n) n = lat;
      return;
    }
    for (const c of coords) visit(c);
  };

  for (const f of features) {
    if (f.geometry && 'coordinates' in f.geometry) visit(f.geometry.coordinates);
  }
  if (!Number.isFinite(w) || !Number.isFinite(e)) return null;
  return [
    [w, s],
    [e, n],
  ];
}

/**
 * Polygons for every jurisdiction we hold permits for (#171): 5-digit county
 * rows from the county topology, places/cousubs from the jurisdiction
 * GeoJSON. `covered` = credited (not thin/partial), which colours it.
 */
export function heldJurisdictionFeatures(
  jurisdictions: ReadonlyArray<{ place_geoid: string; thin_partial?: boolean | undefined }>,
  placeIndex: ReadonlyMap<string, Feature>,
  countyIndex: ReadonlyMap<string, Feature>,
): GeoJSON.FeatureCollection {
  const features: Feature[] = [];
  for (const j of jurisdictions) {
    const g = j.place_geoid;
    const base = g.length === 5 ? countyIndex.get(g) : placeIndex.get(g);
    if (!base) continue;
    features.push({
      ...base,
      properties: { ...(base.properties ?? {}), place_geoid: g, covered: !j.thin_partial },
    });
  }
  return { type: 'FeatureCollection', features };
}
